// Enum-значения соответствуют `packages/db/prisma/schema.prisma` (ВЕРХНИЙ_РЕГИСТР).
// Русские подписи для UI собраны в соответствующих Record-словарях ниже.

export type Role = "DIRECTOR" | "ADMIN" | "ACCOUNTANT" | "TEACHER" | "STUDENT";

export const ROLE_LABELS: Record<Role, string> = {
  DIRECTOR: "Директор",
  ADMIN: "Администратор",
  ACCOUNTANT: "Бухгалтер",
  TEACHER: "Преподаватель",
  STUDENT: "Ученик",
};

export type UserStatus = "ACTIVE" | "PENDING";

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  ACTIVE: "Активен",
  PENDING: "Ожидает подтверждения",
};

export type TeacherStatus = "ACTIVE" | "PENDING" | "INACTIVE";

export const TEACHER_STATUS_LABELS: Record<TeacherStatus, string> = {
  ACTIVE: "Работает",
  PENDING: "Ожидает подтверждения",
  INACTIVE: "Неактивен",
};

export type StudentStatus = "ACTIVE" | "VACATION" | "LEFT";

export const STUDENT_STATUS_LABELS: Record<StudentStatus, string> = {
  ACTIVE: "Учится",
  VACATION: "Каникулы",
  LEFT: "Ушёл",
};

export type LeadSource =
  | "BANNER"
  | "INSTAGRAM"
  | "TELEGRAM"
  | "FRIENDS"
  | "WORD_OF_MOUTH"
  | "WALK_IN"
  | "WEBSITE"
  | "OTHER";

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  BANNER: "Баннер",
  INSTAGRAM: "Instagram",
  TELEGRAM: "Telegram",
  FRIENDS: "Знакомые",
  WORD_OF_MOUTH: "Сарафан",
  WALK_IN: "Проходил мимо",
  WEBSITE: "Сайт",
  OTHER: "Другое",
};

export type GroupStatus = "NEW" | "RECRUITING" | "CLOSED" | "ARCHIVED";

export const GROUP_STATUS_LABELS: Record<GroupStatus, string> = {
  NEW: "Новая",
  RECRUITING: "Идёт набор",
  CLOSED: "Закрытая",
  ARCHIVED: "Архив",
};

/** Группы, в которые можно зачислять учеников. */
export const GROUP_STATUSES_OPEN: GroupStatus[] = ["NEW", "RECRUITING"];

export type EnrollmentStatus = "ACTIVE" | "LEFT";

export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = {
  ACTIVE: "Учится",
  LEFT: "Ушёл",
};

export type AttendanceStatus = "PRESENT" | "LEFT" | "EXCUSED" | "UNEXCUSED" | "CANCELED";

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  PRESENT: "Пришёл",
  LEFT: "Ушёл",
  EXCUSED: "Уважительная",
  UNEXCUSED: "Неуважительная",
  CANCELED: "Отменён",
};

/** Статусы, доступные для выбора в журнале (в порядке отображения). */
export const ATTENDANCE_MARKABLE: AttendanceStatus[] = ["PRESENT", "LEFT", "EXCUSED", "UNEXCUSED"];

/** Присутствием считается только «Пришёл»; остальные статусы различают причину отсутствия. */
export const ATTENDANCE_COUNTED: AttendanceStatus[] = ["PRESENT"];

export function isPresentStatus(status: AttendanceStatus | string): boolean {
  return ATTENDANCE_COUNTED.includes(status as AttendanceStatus);
}

/** Оценка выставляется только при присутствии на уроке. */
export function allowsGrade(status: AttendanceStatus | string): boolean {
  return isPresentStatus(status);
}

/** Цвета статусов повторяют окраску рабочей таблицы центра. */
export const ATTENDANCE_STATUS_COLORS: Record<AttendanceStatus, { bg: string; fg: string }> = {
  PRESENT: { bg: "var(--ok-soft)", fg: "var(--ok)" },
  LEFT: { bg: "var(--surface-2)", fg: "var(--text-3)" },
  EXCUSED: { bg: "var(--warn-soft)", fg: "var(--warn)" },
  UNEXCUSED: { bg: "var(--crit-soft)", fg: "var(--crit)" },
  CANCELED: { bg: "var(--surface-2)", fg: "var(--text-3)" },
};

export type PaymentMethod = "CASH" | "CARD" | "CLICK" | "TRANSFER";

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  CASH: "Наличные",
  CARD: "Карта",
  CLICK: "Click/Payme",
  TRANSFER: "Перечисление",
};

export type ExpenseKind = "HOUSE" | "ADMIN";

export const EXPENSE_KIND_LABELS: Record<ExpenseKind, string> = {
  HOUSE: "Хозяйственный",
  ADMIN: "Административный",
};

export type SalaryPayoutType = "SALARY" | "ADVANCE";

export const SALARY_PAYOUT_TYPE_LABELS: Record<SalaryPayoutType, string> = {
  SALARY: "Зарплата",
  ADVANCE: "Аванс",
};

export type LeadStage = "NEW" | "CALLED" | "BOOKED" | "TRIAL" | "WON" | "LOST";

export const LEAD_STAGE_LABELS: Record<LeadStage, string> = {
  NEW: "Новая заявка",
  CALLED: "Связались",
  BOOKED: "Записан на пробный",
  TRIAL: "Пробный проведён",
  WON: "Зачислен",
  LOST: "Отказ",
};

export const LEAD_STAGES_ORDER: LeadStage[] = ["NEW", "CALLED", "BOOKED", "TRIAL", "WON", "LOST"];

export type NotificationChannel = "SMS" | "TELEGRAM";

export const NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> = {
  SMS: "SMS",
  TELEGRAM: "Telegram",
};

export type NotificationStatus = "QUEUED" | "SENT" | "FAILED";

export const NOTIFICATION_STATUS_LABELS: Record<NotificationStatus, string> = {
  QUEUED: "В очереди",
  SENT: "Отправлено",
  FAILED: "Ошибка",
};

export type DueDayMode = "FIXED_FIRST_DAY" | "INDIVIDUAL";

export const DUE_DAY_MODE_LABELS: Record<DueDayMode, string> = {
  FIXED_FIRST_DAY: "1-е число для всех",
  INDIVIDUAL: "Индивидуально от даты прихода",
};

// Начисление (Charge) — вычисляемый статус, не хранится в БД (см. ТЗ §7.2).
export type ChargeStateStatus = "paid" | "partial" | "overdue" | "due";

export const CHARGE_STATE_LABELS: Record<ChargeStateStatus, string> = {
  paid: "Оплачено",
  partial: "Частично",
  overdue: "Просрочено",
  due: "Ожидает",
};

// Набор дней недели группы. Схема хранит `days: Int[]` (JS Date.getDay(): 0=вс..6=сб),
// но форма создания/редактирования группы в MVP предлагает ровно 2 варианта (ТЗ §9.5).
export type Dayset = "MON_WED_FRI" | "TUE_THU_SAT";

export const DAYSET_LABELS: Record<Dayset, string> = {
  MON_WED_FRI: "Пн-Ср-Пт",
  TUE_THU_SAT: "Вт-Чт-Сб",
};

export const DAYSET_DAYS: Record<Dayset, number[]> = {
  MON_WED_FRI: [1, 3, 5],
  TUE_THU_SAT: [2, 4, 6],
};

export function daysToDayset(days: number[]): Dayset | null {
  const sorted = [...days].sort().join(",");
  if (sorted === DAYSET_DAYS.MON_WED_FRI.sort().join(",")) return "MON_WED_FRI";
  if (sorted === DAYSET_DAYS.TUE_THU_SAT.sort().join(",")) return "TUE_THU_SAT";
  return null;
}
